import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Stack, Typography, Paper, Box, Collapse, Link, Theme, Button, ButtonBase, Tooltip } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import CodeEmbed from './CodeEmbed';
import type {
  CodeSnippetCodeByDropZone,
  CodeSnippetDropZoneMode,
  CodeSnippetImport,
  CodeSnippetPlacementFilter,
  CodeSnippetSource,
  CodeSnippetSourceFilter,
} from '../types';

type TranslationFunction = ReturnType<typeof useTranslation>['t'];

interface CodeSnippetI18nText {
  key: string;
  defaultValue: string;
  values?: Record<string, string | number>;
}

export interface CodeSnippet {
  key: string;
  title: string;
  titleI18n?: CodeSnippetI18nText;
  icon?: JSX.Element;
  iconComponent?: React.ElementType;
  code?: string;
  insertText?: string;
  display?: string;
  codeByDropZone?: CodeSnippetCodeByDropZone;
  displayByDropZone?: CodeSnippetCodeByDropZone;
  codeLines?: string[];
  displayLines?: string[];
  receiverClassName?: string;
  type?: string;
  dropZone?: CodeSnippetDropZoneMode;
  source?: CodeSnippetSource;
  imports?: CodeSnippetImport[];
  frameworkClassName?: string;
  children?: CodeSnippet[];
  description?: string;
  descriptionI18n?: CodeSnippetI18nText;
  more_info?: string;
  requiresCommandManagement?: boolean;
  disabledReasonI18n?: CodeSnippetI18nText;
}

export interface CodeSnippetCategoryHeader {
  title: string;
  description?: string;
}

interface CodeContentProps {
  snippet: CodeSnippet;
  extraContentTop?: React.ReactNode;
  filterText?: string;
  placementFilter?: CodeSnippetPlacementFilter;
  sourceFilter?: CodeSnippetSourceFilter;
  emptyFilterMessage?: string;
  commandManagementEnabled: boolean;
  hideDisabledSnippets?: boolean;
  hideSnippetCardHeaders?: boolean;
  categoryNavigation?: {
    selectedCategoryKey: string | null;
    onSelectCategory: (key: string) => void;
    onBack: () => void;
    hideBackControl?: boolean;
    hideSelectedCategoryHeader?: boolean;
    onSelectedCategoryHeaderChange?: (header: CodeSnippetCategoryHeader | null) => void;
  };
}

// Normalize markdown from i18n/Locize - ensures newlines are proper for fenced code blocks
const normalizeMarkdown = (s: string): string => (s ?? '').replace(/\\n/g, '\n').replace(/\r\n/g, '\n').replace(/\r/g, '\n');

const interpolateDefault = (defaultValue: string, values: Record<string, string | number> = {}): string =>
  defaultValue.replace(/\{\{(\w+)\}\}/g, (_match, name: string) => String(values[name] ?? ''));

const getSnippetDescription = (snippet: CodeSnippet, t: TranslationFunction): string | undefined => {
  if (!snippet.descriptionI18n) return snippet.description;

  const { key, defaultValue, values } = snippet.descriptionI18n;
  const translated = t(key, { defaultValue, ...values });
  return typeof translated === 'string' ? translated : interpolateDefault(defaultValue, values);
};

const getSnippetTitle = (snippet: CodeSnippet, t: TranslationFunction): string => {
  if (!snippet.titleI18n) return snippet.title;

  const { key, defaultValue, values } = snippet.titleI18n;
  const translated = t(key, { defaultValue, ...values });
  return typeof translated === 'string' ? translated : interpolateDefault(defaultValue, values);
};

const getSnippetDisabledReason = (snippet: CodeSnippet, t: TranslationFunction): string => {
  const fallback = t('snippets.disabled.requiresRobotBase', 'Turn on Use RobotBase to use this snippet.');
  if (!snippet.disabledReasonI18n) return fallback;

  const { key, defaultValue, values } = snippet.disabledReasonI18n;
  const translated = t(key, { defaultValue, ...values });
  return typeof translated === 'string' ? translated : interpolateDefault(defaultValue, values);
};

const isSnippetDisabled = (snippet: CodeSnippet, commandManagementEnabled: boolean): boolean =>
  snippet.requiresCommandManagement === true && !commandManagementEnabled;

const searchableSnippetText = (snippet: CodeSnippet, t: TranslationFunction): string =>
  [
    snippet.key,
    snippet.title,
    getSnippetTitle(snippet, t),
    getSnippetDescription(snippet, t),
    snippet.more_info,
    snippet.code,
    snippet.display,
    ...(snippet.codeLines ?? []),
    ...(snippet.displayLines ?? []),
    ...(snippet.imports ?? []).flatMap((snippetImport) => [snippetImport.module, ...snippetImport.names]),
  ]
    .filter((value): value is string => Boolean(value))
    .join(' ')
    .toLowerCase();

const placementMatches = (dropZone: CodeSnippetDropZoneMode | undefined, placementFilter: CodeSnippetPlacementFilter | undefined): boolean =>
  placementFilter == null || placementFilter === 'all' || dropZone === 'contextual' || dropZone === placementFilter;

const sourceMatches = (source: CodeSnippetSource | undefined, sourceFilter: CodeSnippetSourceFilter | undefined): boolean =>
  sourceFilter == null || sourceFilter === 'all' || source === sourceFilter;

const filterSnippetTree = (
  snippet: CodeSnippet,
  terms: string[],
  t: TranslationFunction,
  placementFilter: CodeSnippetPlacementFilter | undefined,
  sourceFilter: CodeSnippetSourceFilter | undefined,
  commandManagementEnabled: boolean,
  hideDisabledSnippets: boolean,
  inheritedDropZone?: CodeSnippetDropZoneMode,
  inheritedSource?: CodeSnippetSource,
  ancestorTextMatched = false,
  cascadeTextMatches = true
): CodeSnippet | null => {
  const resolvedDropZone = snippet.dropZone ?? inheritedDropZone;
  const resolvedSource = snippet.source ?? inheritedSource;
  const isDisabled = isSnippetDisabled(snippet, commandManagementEnabled);
  const hasChildren = (snippet.children?.length ?? 0) > 0;
  const ownTextMatched = terms.length === 0 || terms.every((term) => searchableSnippetText(snippet, t).includes(term));
  const textMatched = ancestorTextMatched || ownTextMatched;
  const childTextMatched = cascadeTextMatches && (ancestorTextMatched || ownTextMatched);

  const filteredChildren = snippet.children
    ?.map((child) =>
      filterSnippetTree(
        child,
        terms,
        t,
        placementFilter,
        sourceFilter,
        commandManagementEnabled,
        hideDisabledSnippets,
        resolvedDropZone,
        resolvedSource,
        childTextMatched,
        cascadeTextMatches
      )
    )
    .filter((child): child is CodeSnippet => child !== null);
  if (filteredChildren && filteredChildren.length > 0) {
    return {
      ...snippet,
      children: filteredChildren,
    };
  }

  if (hasChildren) {
    return null;
  }

  if (hideDisabledSnippets && isDisabled) {
    return null;
  }

  if (textMatched && placementMatches(resolvedDropZone, placementFilter) && sourceMatches(resolvedSource, sourceFilter)) {
    return snippet;
  }

  return null;
};

const codeForPlacement = (
  item: CodeSnippet,
  placementFilter: CodeSnippetPlacementFilter | undefined,
  field: 'codeByDropZone' | 'displayByDropZone'
): string | undefined => {
  const values = item[field];
  if (!values) return undefined;

  if (placementFilter === 'top-level-setup' || placementFilter === 'action-group') {
    return values[placementFilter];
  }

  return values['action-group'] ?? values['top-level-setup'];
};

// Reusable, theme-aware code renderers for Markdown
const InlineCode = ({ children }: { children: React.ReactNode }) => (
  <Box
    component="code"
    sx={(theme: Theme) => ({
      backgroundColor: 'action.hover',
      px: 0.5,
      borderRadius: 0.5,
      fontSize: (theme.typography as { code?: { fontSize?: string } }).code?.fontSize,
      fontFamily: (theme.typography as { code?: { fontFamily?: string } }).code?.fontFamily,
      lineHeight: 'inherit',
      display: 'inline-block',
    })}
  >
    {children}
  </Box>
);

const BlockCode = ({ children }: { children: React.ReactNode }) => (
  <Box
    component="pre"
    sx={{
      backgroundColor: 'action.hover',
      p: 1,
      borderRadius: 1,
      whiteSpace: 'pre-wrap',
      overflow: 'auto',
    }}
  >
    <Box
      component="code"
      sx={(theme: Theme) => ({
        fontSize: (theme.typography as { code?: { fontSize?: string } }).code?.fontSize,
        fontFamily: (theme.typography as { code?: { fontFamily?: string } }).code?.fontFamily,
      })}
    >
      {children}
    </Box>
  </Box>
);

const CodeContent = (props: CodeContentProps) => {
  const { t } = useTranslation();
  const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({});
  const [disabledTooltipKey, setDisabledTooltipKey] = useState<string | null>(null);
  const categoryNavigation = props.categoryNavigation;
  const selectedCategoryKey = categoryNavigation?.selectedCategoryKey ?? null;
  const onSelectedCategoryHeaderChange = categoryNavigation?.onSelectedCategoryHeaderChange;
  const commandManagementEnabled = props.commandManagementEnabled;
  const hideDisabledSnippets = props.hideDisabledSnippets ?? false;
  const filterTerms = useMemo(() => (props.filterText ?? '').trim().toLowerCase().split(/\s+/).filter(Boolean), [props.filterText]);
  const isSearchActive = filterTerms.length > 0;
  const visibleChildren = useMemo(
    () =>
      props.snippet.children
        ?.map((item) =>
          filterSnippetTree(
            item,
            filterTerms,
            t,
            props.placementFilter,
            props.sourceFilter,
            commandManagementEnabled,
            hideDisabledSnippets,
            props.snippet.dropZone,
            props.snippet.source,
            false,
            !isSearchActive
          )
        )
        .filter((item): item is CodeSnippet => item !== null) ?? [],
    [
      props.snippet.children,
      props.snippet.dropZone,
      props.snippet.source,
      props.placementFilter,
      props.sourceFilter,
      commandManagementEnabled,
      hideDisabledSnippets,
      filterTerms,
      isSearchActive,
      t,
    ]
  );
  const selectedCategory = useMemo(
    () => (!isSearchActive && selectedCategoryKey ? visibleChildren.find((item) => item.key === selectedCategoryKey) ?? null : null),
    [isSearchActive, selectedCategoryKey, visibleChildren]
  );
  const selectedCategoryHeader = useMemo<CodeSnippetCategoryHeader | null>(
    () =>
      selectedCategory
        ? {
            title: getSnippetTitle(selectedCategory, t),
            description: getSnippetDescription(selectedCategory, t),
          }
        : null,
    [selectedCategory, t]
  );

  useEffect(() => {
    onSelectedCategoryHeaderChange?.(selectedCategoryHeader);
  }, [onSelectedCategoryHeaderChange, selectedCategoryHeader]);

  useEffect(() => {
    if (!disabledTooltipKey) return;

    const closeTooltip = () => setDisabledTooltipKey(null);

    window.addEventListener('scroll', closeTooltip, true);
    window.addEventListener('wheel', closeTooltip, true);
    window.addEventListener('touchmove', closeTooltip, true);

    return () => {
      window.removeEventListener('scroll', closeTooltip, true);
      window.removeEventListener('wheel', closeTooltip, true);
      window.removeEventListener('touchmove', closeTooltip, true);
    };
  }, [disabledTooltipKey]);

  const renderSnippetIcon = (item: CodeSnippet) =>
    item.iconComponent ? (
      <Box
        component={item.iconComponent}
        aria-hidden
        sx={{
          width: 20,
          height: 20,
          flexShrink: 0,
          transform: 'translate(2px, -1px)',
          color: 'primary.main',
        }}
      />
    ) : item.icon ? (
      <Box
        sx={{
          width: 20,
          height: 20,
          flexShrink: 0,
          transform: 'translate(2px, -1px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'primary.main',
          '& > *': {
            width: '100%',
            height: '100%',
          },
        }}
      >
        {item.icon}
      </Box>
    ) : null;

  const renderEmptyState = () => (
    <Box textAlign="center" width="100%" p={2}>
      <Typography variant="body2" color="text.secondary">
        {props.emptyFilterMessage ?? t('snippets.filter.empty', 'No snippets match that filter.')}
      </Typography>
    </Box>
  );

  const renderCategoryItem = (item: CodeSnippet) => {
    const title = getSnippetTitle(item, t);
    const description = getSnippetDescription(item, t);

    return (
      <Paper key={`category-${item.key}`} sx={{ width: '100%', borderRadius: 1 }}>
        <ButtonBase
          data-testid={`snippet-category-${item.key}`}
          onClick={() => categoryNavigation?.onSelectCategory(item.key)}
          aria-label={t('snippets.category.openAriaLabel', 'Open {{title}} snippets', { title })}
          sx={{
            width: '100%',
            display: 'block',
            textAlign: 'left',
            p: 1.5,
            borderRadius: 1,
            '& .snippet-category-chevron': {
              color: 'text.secondary',
              opacity: 0.55,
              transform: 'translateX(-2px)',
              transition: (theme) =>
                theme.transitions.create(['color', 'opacity', 'transform'], {
                  duration: theme.transitions.duration.shortest,
                }),
              '@media (hover: hover)': {
                opacity: 0.28,
              },
              '@media (hover: none)': {
                opacity: 0.7,
                transform: 'translateX(0)',
              },
            },
            '&:hover .snippet-category-chevron, &.Mui-focusVisible .snippet-category-chevron': {
              color: 'text.primary',
              opacity: 1,
              transform: 'translateX(0)',
            },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                {renderSnippetIcon(item)}
                <Typography variant="h6" component="div" sx={{ userSelect: 'none', lineHeight: 1.2, minWidth: 0 }}>
                  {title}
                </Typography>
              </Stack>
              {description ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {description}
                </Typography>
              ) : null}
            </Box>
            <Box sx={{ alignItems: 'center', display: 'flex', flexShrink: 0, justifyContent: 'center', width: 20 }}>
              <ChevronRightIcon className="snippet-category-chevron" fontSize="small" />
            </Box>
          </Stack>
        </ButtonBase>
      </Paper>
    );
  };

  const renderCategoryBackControl = () => (
    <Button startIcon={<ArrowBackIcon fontSize="small" />} onClick={props.categoryNavigation?.onBack} sx={{ alignSelf: 'flex-start' }}>
      {t('snippets.category.back', 'Back to categories')}
    </Button>
  );

  const renderSnippetItem = (
    item: CodeSnippet,
    parentType: string | undefined,
    parentDropZone: CodeSnippetDropZoneMode | undefined,
    parentImports: CodeSnippetImport[] | undefined,
    keyPrefix: string,
    options: { hideOwnHeader?: boolean } = {}
  ) => {
    const itemType = item.type ?? parentType;
    const itemDropZone = item.dropZone ?? parentDropZone;
    const itemImports = item.imports ?? parentImports;
    const frameworkClassName = item.frameworkClassName;
    const hasChildren = item.children && item.children.length > 0;
    const isDisabled = isSnippetDisabled(item, commandManagementEnabled);
    const disabledReason = isDisabled ? getSnippetDisabledReason(item, t) : undefined;
    const title = getSnippetTitle(item, t);
    const description = getSnippetDescription(item, t);
    const itemKey = `${keyPrefix}-${item.key}`;
    const isDisabledLeaf = isDisabled && !hasChildren;
    const isLeaf = !hasChildren;
    const showGroupHeader = hasChildren && !options.hideOwnHeader;
    const showLeafHeader = isLeaf && !props.hideSnippetCardHeaders;
    const childSnippets = hasChildren ? (
      <Stack key={itemKey} direction="column" spacing={1.25} sx={{ width: '100%' }}>
        {item.children?.map((child) => renderSnippetItem(child, itemType, itemDropZone, itemImports, `${keyPrefix}-${item.key}`))}
      </Stack>
    ) : null;

    if (hasChildren && options.hideOwnHeader) {
      return childSnippets;
    }

    const snippetItem = (
      <Paper
        key={itemKey}
        aria-disabled={isDisabledLeaf ? true : undefined}
        aria-label={isDisabledLeaf && disabledReason ? `${title}. ${disabledReason}` : undefined}
        sx={{
          height: 'auto',
          padding: isLeaf ? 1.25 : 1.5,
          width: '100%',
          border: isLeaf ? 1 : undefined,
          borderColor: isDisabledLeaf ? 'action.disabledBackground' : 'divider',
          borderRadius: 1.5,
          boxShadow: isLeaf ? 'none' : undefined,
          backgroundColor: isLeaf ? (isDisabledLeaf ? 'action.disabledBackground' : 'action.hover') : undefined,
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          cursor: isDisabledLeaf ? 'not-allowed' : undefined,
          transition: (theme) =>
            theme.transitions.create(['background-color', 'border-color'], {
              duration: theme.transitions.duration.shortest,
            }),
        }}
      >
        {showGroupHeader ? (
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ marginBottom: 1 }}>
            {renderSnippetIcon(item)}
            <Typography variant="h6" component="div" sx={{ userSelect: 'none', lineHeight: 1, flexGrow: 1 }}>
              {title}
            </Typography>
          </Stack>
        ) : null}
        {showLeafHeader ? (
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ marginBottom: 1 }}>
            {renderSnippetIcon(item)}
            <Typography variant="subtitle2" component="div" sx={{ userSelect: 'none', lineHeight: 1.2, flexGrow: 1 }}>
              {title}
            </Typography>
          </Stack>
        ) : null}
        {showGroupHeader && description && (
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            {description}
          </Typography>
        )}
        {hasChildren ? (
          childSnippets
        ) : item.codeLines && item.codeLines.length > 0 ? (
          <Stack direction="column" spacing={1.25}>
            {item.codeLines.map((line, idx) => {
              const isGroup = item.type === 'group';
              const code = isGroup ? `${line}\n)` : `${line},`;
              return (
                <CodeEmbed
                  key={`${item.key}-line-${idx}`}
                  title={line}
                  code={code}
                  type={itemType}
                  dropZone={itemDropZone}
                  imports={itemImports}
                  frameworkClassName={frameworkClassName}
                  display={item.displayLines ? item.displayLines[idx] : undefined}
                  receiverClassName={item.receiverClassName}
                  disabled={isDisabled}
                />
              );
            })}
          </Stack>
        ) : (
          <CodeEmbed
            title={item.title}
            code={item.code ?? item.codeByDropZone?.['top-level-setup'] ?? item.codeByDropZone?.['action-group'] ?? ''}
            insertText={item.insertText}
            type={itemType}
            dropZone={itemDropZone}
            codeByDropZone={item.codeByDropZone}
            imports={itemImports}
            frameworkClassName={frameworkClassName}
            display={codeForPlacement(item, props.placementFilter, 'displayByDropZone') ?? item.display}
            receiverClassName={item.receiverClassName}
            disabled={isDisabled}
          />
        )}
        {!hasChildren && description && (
          <Typography variant="body2" sx={{ marginTop: 1 }}>
            {description}
          </Typography>
        )}
        {item.more_info && (
          <Box sx={{ marginTop: 0.5, width: '100%' }}>
            <Link
              component="button"
              type="button"
              onClick={() =>
                setExpandedMap((prev) => ({
                  ...prev,
                  [item.key]: !prev[item.key],
                }))
              }
              underline="hover"
              sx={{ padding: 0, alignSelf: 'flex-start', fontSize: 'body2.fontSize' }}
            >
              {expandedMap[item.key] ? t('ui.show-less', 'Show less') : t('ui.show-more', 'Show more')}
            </Link>
            <Collapse in={!!expandedMap[item.key]} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  marginTop: 1,
                  '& a': { color: 'primary.main' },
                  '& img': {
                    maxWidth: '100%',
                    height: 'auto',
                    display: 'block',
                    borderRadius: 1,
                  },
                }}
              >
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeSanitize]}
                  components={{
                    a: (props: { href?: string; children?: React.ReactNode }) => {
                      const { href, children } = props;
                      return (
                        <Link href={href ?? '#'} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 'body2.fontSize' }}>
                          {children}
                        </Link>
                      );
                    },
                    p: (props: { children?: React.ReactNode }) => {
                      const { children } = props;
                      return <Typography variant="body2">{children}</Typography>;
                    },
                    img: (props: { src?: string; alt?: string }) => {
                      const { src, alt } = props;
                      return (
                        <Box
                          component="img"
                          src={src}
                          alt={alt ?? ''}
                          draggable={false}
                          sx={{ maxWidth: '100%', height: 'auto', display: 'block', borderRadius: 1 }}
                        />
                      );
                    },
                    code: (props: { inline?: boolean; children?: React.ReactNode }) => {
                      const { inline, children } = props;
                      return inline ? <InlineCode>{children}</InlineCode> : <BlockCode>{children}</BlockCode>;
                    },
                  }}
                >
                  {normalizeMarkdown(item.more_info ?? '')}
                </ReactMarkdown>
              </Box>
            </Collapse>
          </Box>
        )}
      </Paper>
    );

    if (!isDisabledLeaf || !disabledReason) {
      return snippetItem;
    }

    return (
      <Tooltip
        key={itemKey}
        title={disabledReason}
        arrow
        placement="top"
        enterDelay={150}
        enterNextDelay={0}
        open={disabledTooltipKey === itemKey}
        onOpen={() => setDisabledTooltipKey(itemKey)}
        onClose={() => setDisabledTooltipKey(null)}
      >
        {snippetItem}
      </Tooltip>
    );
  };

  const renderSearchResultGroup = (
    item: CodeSnippet,
    parentType: string | undefined,
    parentDropZone: CodeSnippetDropZoneMode | undefined,
    parentImports: CodeSnippetImport[] | undefined,
    keyPrefix: string
  ) => {
    const hasChildren = (item.children?.length ?? 0) > 0;

    if (!hasChildren) {
      return renderSnippetItem(item, parentType, parentDropZone, parentImports, keyPrefix);
    }

    return (
      <Stack key={`${keyPrefix}-${item.key}`} direction="column" spacing={0.75} sx={{ width: '100%' }}>
        <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, fontWeight: 700 }}>
          {getSnippetTitle(item, t)}
        </Typography>
        {renderSnippetItem(item, parentType, parentDropZone, parentImports, keyPrefix, { hideOwnHeader: true })}
      </Stack>
    );
  };

  return (
    <Stack direction="column" width={'100%'} spacing={1} sx={{ padding: 1 }}>
      <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={1} sx={{ width: '100%' }}>
        {props.extraContentTop ? (
          <Box width={'100%'}>{props.extraContentTop}</Box>
        ) : (
          <Box textAlign={'center'} width={'100%'} p={2}>
            <Typography textAlign={'center'}>
              <Trans i18nKey={`${props.snippet.key}.tab.header`}>Text placeholder for the {props.snippet.title} tab</Trans>
            </Typography>
          </Box>
        )}
        {categoryNavigation ? (
          isSearchActive ? (
            visibleChildren.map((item) =>
              renderSearchResultGroup(item, props.snippet.type, props.snippet.dropZone, props.snippet.imports, `search-${props.snippet.key}`)
            )
          ) : selectedCategoryKey ? (
            <>
              {categoryNavigation.hideBackControl ? null : renderCategoryBackControl()}
              {selectedCategory
                ? renderSnippetItem(
                    selectedCategory,
                    props.snippet.type,
                    props.snippet.dropZone,
                    props.snippet.imports,
                    `item-${props.snippet.key}`,
                    {
                      hideOwnHeader: categoryNavigation.hideSelectedCategoryHeader,
                    }
                  )
                : renderEmptyState()}
            </>
          ) : (
            visibleChildren.map((item) => renderCategoryItem(item))
          )
        ) : (
          visibleChildren.map((item) =>
            renderSnippetItem(item, props.snippet.type, props.snippet.dropZone, props.snippet.imports, `item-${props.snippet.key}`)
          )
        )}
        {props.snippet.children &&
          props.snippet.children.length > 0 &&
          visibleChildren.length === 0 &&
          (!selectedCategoryKey || isSearchActive) &&
          renderEmptyState()}
      </Stack>
    </Stack>
  );
};

export default CodeContent;
